import React from 'react'
import lodash from 'lodash'
import * as echarts from 'echarts'
import { makeStyles } from 'tss-react/mui'
import { Logger } from 'wdc-cube'
import { chartValueLabelStyle, classToFComponent } from 'src/utils/views'

import { ChartFontSize, ChartPalette, tooltipStyle } from './helpers/tcm_chart_theme'
import { formatNumberToCompactShortForm } from './helpers/tcm_view_utils'
import { EChartViewClass } from './helpers/base-echart'

import { ChartFilialMatrizScope } from '../tcm_scopes'
import { TcmCard } from 'src/utils/views/modules/the-consumer-market'

const LOG = Logger.get('TCM-FILIAL-MATRIX')

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        alignSelf: 'stretch',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'hidden'
    }
})

type ChartFilialMatrizProps = {
    className?: string
    style?: React.CSSProperties
    scope: ChartFilialMatrizScope
}

class ChartFilialMatrizViewClass extends EChartViewClass<ChartFilialMatrizProps> {
    // :: Fields
    scope!: ChartFilialMatrizScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState(props: ChartFilialMatrizProps) {
        this.scope = props.scope
        this.classes = useStyles().classes
        this.chartData = buildChartData(this.scope)
        this.loaded = this.scope.loaded
        super.onSyncState(props)
    }

    override render({ className, style, scope }: ChartFilialMatrizProps): React.JSX.Element {
        return (
            <TcmCard className={className} style={style} caption={scope.caption} dense>
                {super.render({ className: this.classes.view, scope })}
            </TcmCard>
        )
    }

    protected override onBindChartEvents(chart: echarts.ECharts) {
        chart.off('click', this.emitClickOnSeries)
        chart.on('click', this.emitClickOnSeries)
    }

    emitClickOnSeries = (evt: echarts.ECElementEvent) => {
        if (!lodash.isNumber(evt.seriesIndex)) {
            return
        }

        switch (evt.seriesIndex) {
            case 0:
                this.scope.onItemClicked(false).catch(LOG.caught)
                break
            case 1:
                this.scope.onItemClicked(true).catch(LOG.caught)
                break
        }
    }
}

export default classToFComponent(ChartFilialMatrizViewClass, React)

function buildChartData(scope: ChartFilialMatrizScope): echarts.EChartsOption {
    const total = scope.numMatrizes + scope.numFiliais
    const labelPerctFormat: echarts.LabelFormatterCallback<echarts.DefaultLabelFormatterCallbackParams> = (params) => {
        return Math.round(((params.value as number) / total) * 100.0) + '%'
    }

    const showLabel = (value: number) => total > 0 && value / total >= 0.12

    const filialColor = scope.filiaisSelected ? ChartPalette.selected : ChartPalette.series
    const matrizColor = scope.matrizesSelected ? ChartPalette.selected : ChartPalette.emphasis

    const labelColor = (selected: boolean) => (selected ? '#ffffff' : chartValueLabelStyle.color)

    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow'
            },
            confine: false,
            ...tooltipStyle
        },
        legend: {
            padding: 0,
            itemHeight: 8,
            itemWidth: 8,
            icon: 'circle',
            textStyle: { color: ChartPalette.label, fontSize: ChartFontSize.axisDense }
        },
        grid: {
            left: 6,
            right: 14,
            bottom: 0,
            top: 18,
            containLabel: true
        },
        xAxis: {
            type: 'value',
            axisTick: { show: false },
            axisLine: { show: false },
            splitLine: { show: false },
            splitNumber: 3,
            axisLabel: {
                color: ChartPalette.label,
                fontSize: ChartFontSize.axisDense,
                formatter: (value: number) => formatNumberToCompactShortForm(value)
            }
        },
        yAxis: {
            type: 'category',
            data: ['PJ'],
            show: false
        },
        series: [
            {
                name: 'Filial',
                type: 'bar',
                itemStyle: { color: filialColor },
                stack: 'total',
                barMaxWidth: 16,
                label: {
                    show: showLabel(scope.numFiliais),
                    color: labelColor(scope.filiaisSelected),
                    fontSize: ChartFontSize.axisDense,
                    fontWeight: 700,
                    formatter: labelPerctFormat
                },
                emphasis: {
                    focus: 'series'
                },
                data: [
                    {
                        value: scope.numFiliais,
                        itemStyle: {
                            color: filialColor
                        }
                    }
                ]
            },
            {
                name: 'Matriz',
                type: 'bar',
                itemStyle: { color: matrizColor },
                stack: 'total',
                barMaxWidth: 16,
                label: {
                    show: showLabel(scope.numMatrizes),
                    color: labelColor(scope.matrizesSelected),
                    fontSize: ChartFontSize.axisDense,
                    fontWeight: 700,
                    formatter: labelPerctFormat
                },
                emphasis: {
                    focus: 'series'
                },
                data: [
                    {
                        value: scope.numMatrizes,
                        itemStyle: {
                            color: matrizColor
                        }
                    }
                ]
            }
        ]
    }
}
